import React, { useState, useEffect } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { Row, Col, Nav, Card, Button, Spinner } from "react-bootstrap";
import { fetchReportPrograms, fetchPrograms } from "../../../services/httpClient";
import { ProgramPieChart } from "./ProgramPieChart";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  level: string;
  level_id?: string | number;
  level_order: number;
  program: string;
  program_id?: string | number;
}

interface LevelPieChartProps {
  data?: RawDataItem[];
}

export const LevelPieChart = ({ data }: LevelPieChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedProgramId, setSelectedProgramId] = useState<string | number | null>(0);
  const [fetchedData, setFetchedData] = useState<RawDataItem[] | null>(null);
  const [showHeading, setShowHeading] = useState<boolean>(false);

  // Fetch data if not provided
  useEffect(() => {
    if (!data) {
      fetchReportPrograms()
        .then((res: any) => {
          const progData = res[0]?.data ?? [];
          const sortedProgData = [...progData].sort((a: any, b: any) => {
            if (a.program < b.program) return -1;
            if (a.program > b.program) return 1;
            if (!a.level_order) return 1;
            if (!b.level_order) return -1;
            if (a.level_order < b.level_order) return -1;
            if (a.level_order > b.level_order) return 1;
            return 0;
          });

          fetchPrograms()
            .then((res: any) => {
              const programs = res[0]?.data ?? [];

              programs.flatMap((prog: any) => {
                const levels = prog.programLevels ?? [];

                // Add level name to each programData entry
                sortedProgData.forEach((d: any) => {
                  if (d.program_id === prog.id) {
                    const level = levels.find((l: any) => l.id === d.level_id);
                    d.level = level?.name;
                  }
                });
              });
              setFetchedData(sortedProgData);
              setShowHeading(true);
            })
            .catch((err: any) => console.log(err))
            .finally(() => {
              setIsLoading(false);
            });
        })
        .catch((err: any) => console.log(err));
    } else {
      setIsLoading(false);
    }
  }, [data]);

  const chartSource = data ?? fetchedData ?? [];
  const programGroups = groupByProgram(chartSource);
  const levelGroups = groupByLevel(chartSource, selectedProgramId ?? undefined);

  function groupByProgram(data: RawDataItem[]) {
    const counts: Record<string, { name: string; count: number }> = {};
    data.forEach((item) => {
      const key = item.program_id ?? item.program;
      const name = item.program;
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts).map(([id, { name, count }]) => ({ id, label: name, numberCases: count }));
  }

  function groupByLevel(data: RawDataItem[], programId?: string | number) {
    const filtered = programId ? data.filter((item) => item.program_id == programId) : data;
    const counts: Record<string, { name: string; count: number; order: number }> = {};
    filtered.forEach((item) => {
      const key = item.level_id ?? item.level ?? "No Level";
      const name = item.level && item.level.trim() !== "" ? item.level : "No Level";
      const order = item.level && item.level.trim() !== "" ? item.level_order : 999;
      if (!counts[key]) {
        counts[key] = { name, count: 0, order };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts)
      .map(([id, { name, count, order }]) => ({ id, label: name, numberCases: count, level_order: order }))
      .sort((a: any, b: any) => a.level_order - b.level_order);
  }

  const chartData = {
    labels: levelGroups.map((item) => item.label),
    datasets: [
      {
        label: "# People",
        data: levelGroups.map((item) => item.numberCases),
        backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      title: {
        display: true,
        text: "Level Assignment",
      },
      legend: {
        display: true,
        position: "left" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
        },
      },
    },
    layout: {
      padding: {
        top: 0,
        bottom: 0,
      },
    },
  };

  if (isLoading) {
    return (
      <Card style={{ minHeight: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Card.Body>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Card.Body>
      </Card>
    );
  }

  if (!chartSource.length) {
    return (
      <Card style={{ minHeight: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Card.Body>
          <p className="mt-2">No Program Records</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {showHeading && <h5 className="app-header-text">Programs and Levels</h5>}
      <Row className="justify-content-start mb-3">
        <Col xs={5} lg={4}>
          <Nav variant="pills" className="flex-column">
            <Nav.Item className="d-grid" key={0}>
              <Nav.Link
                eventKey={0}
                className={selectedProgramId === 0 ? "nav-vertical-active" : "nav-vertical"}
                onClick={() => setSelectedProgramId(0)}
                style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
              >
                {"All Programs"}
              </Nav.Link>
            </Nav.Item>
            {programGroups.map((program) => (
              <Nav.Item className="d-grid" key={program.id}>
                <Nav.Link
                  eventKey={program.id}
                  className={selectedProgramId === program.id ? "nav-vertical-active" : "nav-vertical"}
                  onClick={() => setSelectedProgramId(program.id)}
                  style={{ textAlign: "left", fontWeight: 500, cursor: "pointer" }}
                >
                  {program.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xs={7} lg={8}>
          {selectedProgramId === 0 ? (
            <ProgramPieChart data={data} />
          ) : (
            <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Pie data={chartData} options={options} width={350} height={350} style={{ maxHeight: "100%", maxWidth: "100%" }} />
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};
